/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';

interface TypographyLayoutsTabProps {
    theme: 'light' | 'dark';
}

const TYPOGRAPHY_EXAMPLE_CODE = `<h1>Heading 1</h1>
<h2>Heading 2</h2>
<h3>Heading 3</h3>
<h4>Heading 4</h4>
<h5>Heading 5</h5>
<h6>Heading 6</h6>
<p>
    Paragraph text shows the shared base typography for normal content blocks.
    It uses the default tag styling from src/styles/base/typography.css.
</p>`;

function TypographyLayoutsTab({ theme }: TypographyLayoutsTabProps) {
    return (
        <div className="layouts-tab">
            <LayoutSectionHeader sectionKey="typography" />

            <section className="layouts-section">
                <h3 className="text-xl font-semibold">Base tag typography</h3>
                <p className="text-base-content/70">
                    This example renders the plain HTML tags `h1` to `h6` and `p` without
                    card-specific overrides.
                </p>

                <h4 className="tracking-wide">Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button type="button" className="tab tab-active">
                            JSX
                        </button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={TYPOGRAPHY_EXAMPLE_CODE}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>

                <h4 className="tracking-wide">Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel items-start justify-start p-6">
                    <div className="w-full">
                        <h1>Heading 1</h1>
                        <h2>Heading 2</h2>
                        <h3>Heading 3</h3>
                        <h4>Heading 4</h4>
                        <h5>Heading 5</h5>
                        <h6>Heading 6</h6>
                        <p>
                            Paragraph text shows the shared base typography for normal content
                            blocks. It uses the default tag styling from
                            {' '}
                            <code>src/styles/base/typography.css</code>
                            .
                        </p>
                    </div>
                </section>
            </section>
        </div>
    );
}

export default TypographyLayoutsTab;
